import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import BAULT_ABI from 'abis/Bault.json'
import BAULT_ROUTER_ABI from 'abis/BaultRouter.json'
import KODIAK_FARM_ABI from 'abis/KodiakFarm.json'
import REWARD_VAULT_ABI from 'abis/RewardVault.json'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { useConnection } from 'hooks/useConnection'
import { KodiakFarmStake } from 'hooks/useKodiakFarm'
import { useWriteContract } from 'hooks/useTransaction'
import sumCurrencyAmount from 'lib/utils/sumCurrencyAmount'
import { useCallback, useEffect, useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { Address, encodeFunctionData } from 'viem'
import { useReadContract } from 'wagmi'

export const useOnUnstakeCallback = (
  stakesToWithdraw: KodiakFarmStake[],
  farmAddress: Address | undefined,
  clear?: () => void
) => {
  const { writeContractAsync } = useWriteContract()
  const { wagmiPublicClient, account } = useConnection()
  const [attemptingTxnStake, setAttemptingTxnStake] = useState(false)
  const [hashStake, setHashStake] = useState('')
  const [willUseEmergency, setWillUseEmergency] = useState<boolean | null>(null)
  const addTransaction = useTransactionAdder()

  const { data: rewardCollectionPaused } = useReadContract({
    address: farmAddress,
    abi: KODIAK_FARM_ABI,
    functionName: 'rewardsCollectionPaused',
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!farmAddress,
    },
  })

  const checkIfEmergencyNeeded = useCallback(async () => {
    if (!farmAddress || !stakesToWithdraw?.length || !wagmiPublicClient || !account) {
      setWillUseEmergency(null)
      return
    }

    // If rewards are paused, definitely will use emergency
    if (rewardCollectionPaused) {
      setWillUseEmergency(true)
      return
    }

    setWillUseEmergency(false)
  }, [farmAddress, stakesToWithdraw, rewardCollectionPaused, wagmiPublicClient, account])

  useEffect(() => {
    checkIfEmergencyNeeded()
  }, [checkIfEmergencyNeeded])

  const onConfirmWithdrawStakes = async () => {
    if (!farmAddress || !stakesToWithdraw?.length || !wagmiPublicClient || !account) return

    setAttemptingTxnStake(true)
    try {
      const kek_ids = stakesToWithdraw.map((stake) => stake.kekId)
      let hash

      if (rewardCollectionPaused) {
        // Use emergency withdraw if rewards collection is paused
        const calls = kek_ids.map((kek_id) =>
          encodeFunctionData({
            abi: KODIAK_FARM_ABI,
            functionName: 'emergencyWithdraw',
            args: [kek_id],
          })
        )
        const txn = {
          address: farmAddress,
          abi: KODIAK_FARM_ABI,
          functionName: 'multicall',
          args: [calls],
        }
        const gas = await wagmiPublicClient.estimateContractGas({ ...txn, account })
        hash = await writeContractAsync({
          ...txn,
          gas: calculateGasMargin(gas as bigint) as bigint,
        })
      } else {
        const txn = {
          address: farmAddress,
          abi: KODIAK_FARM_ABI,
          functionName: 'withdrawLockedMultiple',
          args: [kek_ids],
        }
        const gas = await wagmiPublicClient.estimateContractGas({ ...txn, account })
        hash = await writeContractAsync({
          ...txn,
          gas: calculateGasMargin(gas as bigint) as bigint,
        })
      }

      setHashStake(hash)
      const sum = sumCurrencyAmount(stakesToWithdraw.map((item) => item.liquidity))

      addTransaction(
        { hash },
        {
          type: TransactionType.UNSTAKE_LIQUIDITY_ISLAND,
          amount: `${sum?.toSignificant(6) || 0} ${sum?.currency?.symbol}`,
        }
      )
    } catch (e) {
      console.error(e)
    } finally {
      setAttemptingTxnStake(false)
    }
  }

  return {
    onConfirmWithdrawStakes,
    attemptingTxn: attemptingTxnStake,
    hash: hashStake,
    willForfeitRewards: willUseEmergency === true,
    clear: () => {
      setAttemptingTxnStake(false)
      setHashStake('')
      clear && clear()
    },
  }
}

/**
 * Hook for unstaking from a vault (without locked stakes)
 */
export const useOnVaultUnstakeCallback = (
  amountToWithdraw: CurrencyAmount<any> | undefined,
  vaultAddress: Address | undefined,
  clear?: () => void
) => {
  const { writeContractAsync } = useWriteContract()
  const { wagmiPublicClient, account } = useConnection()
  const [attemptingTxnStake, setAttemptingTxnStake] = useState(false)
  const [hashStake, setHashStake] = useState('')
  const addTransaction = useTransactionAdder()

  const onConfirmWithdraw = useCallback(async () => {
    if (!amountToWithdraw || !vaultAddress || !wagmiPublicClient) return

    setAttemptingTxnStake(true)
    try {
      const txn = {
        address: vaultAddress,
        abi: REWARD_VAULT_ABI,
        functionName: 'withdraw',
        args: [BigInt(amountToWithdraw.quotient.toString())],
        account,
      }
      const gas = await wagmiPublicClient.estimateContractGas(txn)
      const hash = await writeContractAsync({
        ...txn,
        gas: calculateGasMargin(gas as bigint) as bigint,
      })

      setHashStake(hash)

      addTransaction(
        { hash },
        {
          type: TransactionType.UNSTAKE_LIQUIDITY_ISLAND,
          amount: `${amountToWithdraw.toSignificant(6)} ${amountToWithdraw.currency.symbol}`,
        }
      )
    } catch (e) {
      console.error(e)
    } finally {
      setAttemptingTxnStake(false)
    }
  }, [vaultAddress, amountToWithdraw, addTransaction, clear, writeContractAsync, wagmiPublicClient, account])

  return {
    onConfirmWithdrawStakes: onConfirmWithdraw,
    attemptingTxn: attemptingTxnStake,
    hash: hashStake,
    clear: useCallback(() => {
      setAttemptingTxnStake(false)
      setHashStake('')
      clear && clear()
    }, [clear]),
  }
}

/**
 * Hook for redeeming from a Bault (auto-compounding vault)
 */
export const useOnBaultRedeemCallback = (
  amountToRedeem: CurrencyAmount<Token> | undefined,
  baultAddress: Address | undefined,
  baultRouterAddress: Address | undefined,
  userAddress: Address | undefined,
  clear?: () => void
) => {
  const { writeContractAsync } = useWriteContract()
  const { wagmiPublicClient, account } = useConnection()
  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [hash, setHash] = useState('')
  const addTransaction = useTransactionAdder()

  const onConfirmRedeem = useCallback(async () => {
    if (!amountToRedeem || !baultRouterAddress || !baultAddress || !wagmiPublicClient) {
      return
    }

    setAttemptingTxn(true)
    try {
      const expectedAsset = await wagmiPublicClient.readContract({
        address: baultAddress,
        abi: BAULT_ABI,
        functionName: 'previewRedeem',
        args: [BigInt(amountToRedeem.quotient.toString())],
      })

      const minAssetsOut = (BigInt(expectedAsset as string) * 990n) / 1000n // 1% slippage

      const txn = {
        address: baultRouterAddress,
        abi: BAULT_ROUTER_ABI,
        functionName: 'redeemFromBault',
        args: [baultAddress, BigInt(amountToRedeem.quotient.toString()), minAssetsOut, userAddress],
        account,
      }
      const gas = await wagmiPublicClient.estimateContractGas(txn)

      // Call redeem function on the Bault Router
      const txHash = await writeContractAsync({
        ...txn,
        gas: calculateGasMargin(gas as bigint) as bigint,
      })

      setHash(txHash)

      addTransaction(
        { hash: txHash },
        {
          type: TransactionType.REDEEM_FROM_BAULT,
          amount: `${amountToRedeem.toSignificant(6)} ${amountToRedeem.currency.symbol}`,
          baultAddress,
        }
      )
    } catch (e) {
      console.error('Error redeeming from Bault:', e)
    } finally {
      setAttemptingTxn(false)
    }
  }, [
    baultAddress,
    baultRouterAddress,
    amountToRedeem,
    userAddress,
    addTransaction,
    writeContractAsync,
    wagmiPublicClient,
    account,
  ])

  return {
    onConfirmRedeem,
    attemptingTxn,
    hash,
    clear: useCallback(() => {
      setAttemptingTxn(false)
      setHash('')
      clear && clear()
    }, [clear]),
  }
}
